import React, { useEffect, useState } from 'react';
import { MapPin, Navigation, CheckCircle } from 'lucide-react';
import { AddressGroup } from '../../types/workOrders';
import { createGoogleMapsUrl } from '../../utils/maps';
import { getAllActiveTasks } from '../../utils/firebase';
import { useAuthStore } from '../../store/authStore';

interface AddressCardProps {
  addressGroup: AddressGroup;
  onClick: () => void;
  isCompleted?: boolean;
}

export function AddressCard({ addressGroup, onClick, isCompleted = false }: AddressCardProps) {
  const { workerId } = useAuthStore();
  const [hasActiveTasks, setHasActiveTasks] = useState(false);

  useEffect(() => {
    const checkActiveTasks = async () => {
      if (!workerId) return;
      try {
        const activeTasks = await getAllActiveTasks(workerId);
        const hasActive = activeTasks.some(task => 
          addressGroup.orders.some(order => 
            order.Tasks.some(orderTask => orderTask.id === task.task_id)
          )
        );
        setHasActiveTasks(hasActive);
      } catch (error) {
        console.error('Error checking active tasks:', error);
      }
    };

    if (!isCompleted) {
      checkActiveTasks();
      // Set up polling to check for active tasks
      const interval = setInterval(checkActiveTasks, 30000); // Check every 30 seconds
      return () => clearInterval(interval);
    }
  }, [workerId, addressGroup.orders, isCompleted]);

  const handleDirectionsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(
      createGoogleMapsUrl(addressGroup.address, addressGroup.city),
      '_blank'
    );
  };

  const orderCount = addressGroup.orders.length;
  const orderText = orderCount === 1 ? 'order' : 'orders';

  return (
    <div 
      onClick={onClick}
      className={`card w-full p-6 text-left hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 cursor-pointer ${
        isCompleted ? 'bg-gray-50 dark:bg-gray-900/20' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-8">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className={`rounded-lg p-2 ${
            isCompleted 
              ? 'bg-green-50 dark:bg-green-900/20' 
              : 'bg-primary-50 dark:bg-primary-900/20'
          }`}>
            {isCompleted ? (
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            ) : (
              <MapPin className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="space-y-1">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate pr-4">
                {addressGroup.address}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {addressGroup.city}, {addressGroup.state}
              </p>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {orderCount} {orderText}
              </p>
              {!isCompleted && hasActiveTasks && (
                <div className="h-3 w-3 rounded-full bg-blue-500 animate-pulse" />
              )}
            </div>
          </div>
        </div>
        <button
          onClick={handleDirectionsClick}
          className="btn-secondary flex-shrink-0"
          aria-label="Get directions"
        >
          <Navigation className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}